import React from 'react'
import { CircularProgress } from '@mui/material'
import { useDeviceInfo } from 'hooks/useDeviceInfo'

import AppleWalletSvg from 'images/inline/wallet/apple-wallet.svg'
import GoogleWalletSvg from 'images/inline/wallet/google-wallet.svg'

import * as styles from './WalletButton.module.scss'

export interface WalletButtonProps {
  /**
   * Callback function when the wallet button is clicked
   */
  onClick?: () => void
  /**
   * When provided, the component renders as an <a> (link) instead of a <button>.
   * Useful for server-side redirect endpoints (e.g. backend returns 302/303).
   */
  href?: string
  /**
   * Target attribute when rendered as an <a>
   */
  target?: React.HTMLAttributeAnchorTarget
  /**
   * Rel attribute when rendered as an <a>
   */
  rel?: string
  /**
   * Additional CSS class name for custom styling
   */
  className?: string
  /**
   * Whether the button is disabled
   */
  disabled?: boolean
  /**
   * Whether the button is in a loading state
   */
  isLoading?: boolean
  /**
   * Custom alt text for the wallet button image
   */
  altText?: string
  /**
   * Force a specific wallet type regardless of device detection
   */
  forceWalletType?: 'apple' | 'google'
}

export const WalletButton: React.FC<WalletButtonProps> = ({
  onClick,
  href,
  target,
  rel,
  className = '',
  disabled = false,
  isLoading = false,
  altText,
  forceWalletType,
}) => {
  const detectedDeviceType = useDeviceInfo()

  // Determine which wallet to show
  const walletType =
    forceWalletType || (detectedDeviceType === 'ios' ? 'apple' : 'google')

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || isLoading) {
      event.preventDefault()
      return
    }

    if (onClick) {
      onClick()
    }
  }

  // Determine which SVG to use and alt text
  const WalletSvg = walletType === 'apple' ? AppleWalletSvg : GoogleWalletSvg
  const defaultAltText =
    walletType === 'apple' ? 'Add to Apple Wallet' : 'Add to Google Wallet'

  const content = isLoading ? (
    <div className={styles.loadingContainer}>
      <CircularProgress size={20} className={styles.spinner} />
      <span className={styles.loadingText}>Adding...</span>
    </div>
  ) : (
    <WalletSvg className={styles.walletImage} alt={altText || defaultAltText} />
  )

  if (href) {
    const handleAnchorClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (disabled || isLoading) {
        event.preventDefault()
        return
      }

      if (onClick) {
        onClick()
      }
    }

    return (
      <a
        href={href}
        target={target}
        rel={rel}
        onClick={handleAnchorClick}
        className={`${styles.walletButton} ${
          isLoading ? styles.loading : ''
        } ${className}`}
        aria-label={isLoading ? 'Adding to wallet...' : altText || defaultAltText}
        aria-disabled={disabled || isLoading}
      >
        {content}
      </a>
    )
  }

  return (
    <button
      onClick={handleClick}
      className={`${styles.walletButton} ${
        isLoading ? styles.loading : ''
      } ${className}`}
      disabled={disabled || isLoading}
      type="button"
      aria-label={isLoading ? 'Adding to wallet...' : altText || defaultAltText}
    >
      {content}
    </button>
  )
}

export default WalletButton
